'use client';
import React, { useState } from 'react';
import { Clock, FileWarning, Gavel, TrendingUp } from 'lucide-react';

interface DelinquentAccount {
  parcelId: string;
  address: string;
  county: string;
  owner: string;
  originalTax: number;
  amountDue: number;
  penalty: number;
  daysOverdue: number;
  agingBucket: '30' | '60' | '90+';
  lienStatus: 'none' | 'filed' | 'active' | 'released';
  lastNoticeDate: string;
  noticeCount: number;
}

const DELINQUENT: DelinquentAccount[] = [
  {
    parcelId: 'LR-MON-0117', address: '88 Tubman Blvd, Monrovia', county: 'Montserrado', owner: 'Mary Pewee',
    originalTax: 2070, amountDue: 2277, penalty: 207, daysOverdue: 45, agingBucket: '30',
    lienStatus: 'none', lastNoticeDate: '2024-04-15', noticeCount: 1,
  },
  {
    parcelId: 'LR-NIM-0008', address: 'Ganta Market Zone B', county: 'Nimba', owner: 'Unknown',
    originalTax: 3200, amountDue: 3840, penalty: 640, daysOverdue: 78, agingBucket: '60',
    lienStatus: 'filed', lastNoticeDate: '2024-03-20', noticeCount: 3,
  },
  {
    parcelId: 'LR-LOF-0019', address: 'Voinjama Riverside', county: 'Lofa', owner: 'Contested — 2 claimants',
    originalTax: 1560, amountDue: 2184, penalty: 624, daysOverdue: 142, agingBucket: '90+',
    lienStatus: 'active', lastNoticeDate: '2024-02-10', noticeCount: 5,
  },
  {
    parcelId: 'LR-MAR-0022', address: 'Kakata Industrial Blvd', county: 'Margibi', owner: 'Unknown',
    originalTax: 4800, amountDue: 5280, penalty: 480, daysOverdue: 38, agingBucket: '30',
    lienStatus: 'none', lastNoticeDate: '2024-04-20', noticeCount: 1,
  },
  {
    parcelId: 'LR-SIN-0007', address: 'Greenville Port Area', county: 'Sinoe', owner: 'Unknown',
    originalTax: 6200, amountDue: 9300, penalty: 3100, daysOverdue: 210, agingBucket: '90+',
    lienStatus: 'active', lastNoticeDate: '2024-01-05', noticeCount: 7,
  },
  {
    parcelId: 'LR-GBS-0055', address: 'Buchanan Port Zone', county: 'Grand Bassa', owner: 'LPA Holdings',
    originalTax: 23400, amountDue: 14040, penalty: 2808, daysOverdue: 62, agingBucket: '60',
    lienStatus: 'filed', lastNoticeDate: '2024-03-28', noticeCount: 2,
  },
];

const agingConfig = {
  '30': { color: '#fbbf24', bg: 'rgba(251,191,36,0.1)', label: '30 Days', border: 'rgba(251,191,36,0.3)' },
  '60': { color: '#f97316', bg: 'rgba(249,115,22,0.1)', label: '60 Days', border: 'rgba(249,115,22,0.3)' },
  '90+': { color: '#f87171', bg: 'rgba(248,113,113,0.1)', label: '90+ Days', border: 'rgba(248,113,113,0.3)' },
};

const lienConfig = {
  none: { color: '#94a3b8', label: 'No Lien' },
  filed: { color: '#fbbf24', label: 'Lien Filed' },
  active: { color: '#f87171', label: 'Lien Active' },
  released: { color: '#34d399', label: 'Lien Released' },
};

export default function DelinquencyTrackerPanel() {
  const [filterBucket, setFilterBucket] = useState<string>('all');
  const [filterLien, setFilterLien] = useState<string>('all');

  const filtered = DELINQUENT.filter((d) => {
    const matchBucket = filterBucket === 'all' || d.agingBucket === filterBucket;
    const matchLien = filterLien === 'all' || d.lienStatus === filterLien;
    return matchBucket && matchLien;
  });

  const totalOutstanding = DELINQUENT.reduce((s, d) => s + d.amountDue, 0);
  const totalPenalties = DELINQUENT.reduce((s, d) => s + d.penalty, 0);
  const bucket30 = DELINQUENT.filter((d) => d.agingBucket === '30').length;
  const bucket60 = DELINQUENT.filter((d) => d.agingBucket === '60').length;
  const bucket90 = DELINQUENT.filter((d) => d.agingBucket === '90+').length;

  const formatCurrency = (val: number) => `$${val.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="space-y-4">
      {/* Summary cards */}
      <div className="grid grid-cols-2 lg:grid-cols-5 gap-3">
        <div className="rounded-xl p-3 lg:col-span-2" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
          <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>Total Outstanding</p>
          <p className="text-xl font-bold font-mono-data" style={{ color: '#f87171' }}>{formatCurrency(totalOutstanding)}</p>
          <p className="text-xs mt-0.5" style={{ color: 'var(--dark-muted)' }}>Penalties: {formatCurrency(totalPenalties)}</p>
        </div>
        {[
          { bucket: '30', count: bucket30, label: '30-Day Overdue' },
          { bucket: '60', count: bucket60, label: '60-Day Overdue' },
          { bucket: '90+', count: bucket90, label: '90+ Day Overdue' },
        ].map(({ bucket, count, label }) => {
          const cfg = agingConfig[bucket as keyof typeof agingConfig];
          return (
            <div key={bucket} className="rounded-xl p-3" style={{ backgroundColor: 'var(--dark-card)', border: `1px solid ${cfg.border}` }}>
              <p className="text-xs mb-1" style={{ color: 'var(--dark-muted)' }}>{label}</p>
              <p className="text-xl font-bold font-mono-data" style={{ color: cfg.color }}>{count}</p>
              <p className="text-xs mt-0.5" style={{ color: cfg.color }}>accounts</p>
            </div>
          );
        })}
      </div>

      {/* Filters */}
      <div className="flex flex-wrap items-center gap-3">
        <div className="flex items-center gap-1.5">
          <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Aging:</span>
          {['all', '30', '60', '90+'].map((b) => (
            <button
              key={b}
              onClick={() => setFilterBucket(b)}
              className="px-2.5 py-1 rounded-full text-xs font-medium transition-all"
              style={{
                backgroundColor: filterBucket === b ? 'rgba(212,160,23,0.2)' : 'var(--dark-elevated)',
                color: filterBucket === b ? 'var(--accent)' : 'var(--dark-muted)',
                border: `1px solid ${filterBucket === b ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
              }}
            >
              {b === 'all' ? 'All' : `${b} days`}
            </button>
          ))}
        </div>
        <div className="flex items-center gap-1.5">
          <span className="text-xs" style={{ color: 'var(--dark-muted)' }}>Lien:</span>
          {['all', 'none', 'filed', 'active'].map((l) => (
            <button
              key={l}
              onClick={() => setFilterLien(l)}
              className="px-2.5 py-1 rounded-full text-xs font-medium transition-all capitalize"
              style={{
                backgroundColor: filterLien === l ? 'rgba(212,160,23,0.2)' : 'var(--dark-elevated)',
                color: filterLien === l ? 'var(--accent)' : 'var(--dark-muted)',
                border: `1px solid ${filterLien === l ? 'rgba(212,160,23,0.4)' : 'var(--dark-border)'}`,
              }}
            >
              {l === 'all' ? 'All' : l}
            </button>
          ))}
        </div>
      </div>

      {/* Delinquency table */}
      <div className="rounded-xl overflow-hidden" style={{ backgroundColor: 'var(--dark-card)', border: '1px solid var(--dark-border)' }}>
        <div className="overflow-x-auto">
          <table className="w-full text-xs">
            <thead>
              <tr style={{ borderBottom: '1px solid var(--dark-border)' }}>
                {['Parcel', 'Owner', 'County', 'Original Tax', 'Amount Due', 'Penalty', 'Days Overdue', 'Aging', 'Lien Status', 'Notices'].map((h) => (
                  <th key={h} className="px-3 py-2.5 text-left font-semibold whitespace-nowrap" style={{ color: 'var(--dark-muted)' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {filtered.map((d) => {
                const agingCfg = agingConfig[d.agingBucket];
                const lienCfg = lienConfig[d.lienStatus];
                return (
                  <tr key={d.parcelId} className="transition-colors hover:bg-white/3"
                    style={{ borderBottom: '1px solid var(--dark-border)' }}>
                    <td className="px-3 py-2.5 font-mono-data font-bold" style={{ color: 'var(--accent)' }}>{d.parcelId}</td>
                    <td className="px-3 py-2.5 max-w-[120px] truncate" style={{ color: 'var(--dark-text)' }}>{d.owner}</td>
                    <td className="px-3 py-2.5" style={{ color: 'var(--dark-muted)' }}>{d.county}</td>
                    <td className="px-3 py-2.5 font-mono-data" style={{ color: 'var(--dark-text)' }}>{formatCurrency(d.originalTax)}</td>
                    <td className="px-3 py-2.5 font-mono-data font-semibold" style={{ color: '#f87171' }}>{formatCurrency(d.amountDue)}</td>
                    <td className="px-3 py-2.5 font-mono-data" style={{ color: '#f97316' }}>+{formatCurrency(d.penalty)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <Clock size={11} style={{ color: agingCfg.color }} />
                        <span style={{ color: agingCfg.color }}>{d.daysOverdue}d</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <span className="px-2 py-0.5 rounded-full font-semibold"
                        style={{ backgroundColor: agingCfg.bg, color: agingCfg.color }}>
                        {agingCfg.label}
                      </span>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        {d.lienStatus !== 'none' && <Gavel size={11} style={{ color: lienCfg.color }} />}
                        <span style={{ color: lienCfg.color }}>{lienCfg.label}</span>
                      </div>
                    </td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center gap-1">
                        <FileWarning size={11} style={{ color: 'var(--dark-muted)' }} />
                        <span style={{ color: 'var(--dark-muted)' }}>{d.noticeCount} sent</span>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Penalty accrual note */}
      <div className="flex items-start gap-2 p-3 rounded-lg" style={{ backgroundColor: 'rgba(248,113,113,0.08)', border: '1px solid rgba(248,113,113,0.2)' }}>
        <TrendingUp size={14} className="flex-shrink-0 mt-0.5" style={{ color: '#f87171' }} />
        <p className="text-xs" style={{ color: 'var(--dark-muted)' }}>
          <span className="font-semibold" style={{ color: '#f87171' }}>Penalty Accrual Policy:</span> 10% penalty applied at 30 days, additional 10% at 60 days, 20% at 90+ days. Liens are automatically filed at 60-day threshold per LRA enforcement protocol.
        </p>
      </div>
    </div>
  );
}
